import { SettingsButton } from "./SettingsButton";
import { Logo } from "./Logo";
import { ProjectName } from "./ProjectName";
import { SavedDesigns } from "./SavedDesigns";
import { CanvasButton } from "./CanvasButton";
import { StudioButton } from "./StudioButton";
import { observer } from "mobx-react-lite";
import type { AutoSave } from "src/types/AutoSave";
import type { Editor } from "src/types/Editor";
import { useEffect, useState } from "react";
import classNames from "classnames";
import type { BooleanState } from "src/types/BooleanState";
import { makeAutoObservable, runInAction } from "mobx";
import { Throttle } from "src/types/Throttle";

// delay for the transition of the navigator in ms

class BooleanDelayState {
  static useState(defaultValue: boolean, delay: number) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [value] = useState(() => new BooleanDelayState(defaultValue, delay));
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => () => value.destroy(), [value]);
    return value;
  }
  private _isOn: boolean;
  private throttle: Throttle;
  constructor(defaultValue: boolean, delay: number) {
    this._isOn = defaultValue;
    this.throttle = new Throttle(delay, false);
    makeAutoObservable(this, {}, { autoBind: true });
  }
  set(value: boolean) {
    this.throttle.run(async () =>
      runInAction(() => {
        this._isOn = value;
      })
    );
  }
  toggle() {
    this.set(!this._isOn);
  }
  on() {
    this.set(true);
  }
  off() {
    this.set(false);
  }
  get isOff() {
    return !this._isOn;
  }
  get isOn() {
    return this._isOn;
  }
  destroy() {
    this.throttle.destroy();
  }
}

export const Navigator = observer(function Navigator(props: { editor: Editor }) {
  const expand = BooleanDelayState.useState(false, 300);
  return (
    <div className="flex flex-col relative bg-white w-[40px] shrink-0">
      <div
        className={classNames(
          "transition-all duration-200 ease-out h-full bg-white flex flex-col z-50 border-black/20 border-r shadow-elevation-2",
          expand.isOff && "w-[41px]",
          expand.isOn && "w-[237px]"
        )}
        onMouseEnter={expand.on}
        onMouseLeave={expand.off}
      >
        <Header expand={expand} />
        <SavedDesigns editor={props.editor} expand={expand} />
        <AutoSaveIndicator autoSave={props.editor.autoSave} />
      </div>
    </div>
  );
});

const Header = observer(function Header(props: { expand: BooleanState }) {
  return (
    <div className="flex flex-col items-center justify-center space-y-[12px] py-[12px] border-b border-neutral-4">
      <Logo expand={props.expand} />
      <ProjectName expanded={props.expand} />
      <div
        className={classNames(
          "flex flex-col space-y-1 w-full",
          props.expand.isOn && "px-[8px]",
          props.expand.isOff && "px-[6px]"
        )}
      >
        <CanvasButton expand={props.expand} />
        <StudioButton expand={props.expand} />
        <SettingsButton expand={props.expand} />
      </div>
    </div>
  );
});

const AutoSaveIndicator = observer(function AutoSave(props: { autoSave: AutoSave }) {
  useEffect(() => {
    const beforeUnload = function (e: BeforeUnloadEvent) {
      if (!props.autoSave.isSaving) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [props.autoSave.isSaving]);
  if (!props.autoSave.isSaving) return null;
  return (
    <div className="p-2">
      <div className="w-full h-full overflow-hidden flex flex-col items-center justify-center">
        <div className="animate-spin inline-block w-4 h-4 border-[2px] border-l-neutral-6 border-t-transparent border-b-neutral-6/60 border-r-neutral-6/0 text-blue-600 rounded-full" />
      </div>
    </div>
  );
});
